import {
  useEffect,
  useState,
  useCallback,
  memo,
  FC,
  useRef,
  useMemo,
} from 'react'
import { GrMoon, GrSun } from 'react-icons/gr'
import { Button } from '../general'
import { getDayNames } from './day-utils'
import { classNames } from '@app/utils/classes'
import { useTranslation } from '@app/lib/hooks/useTranslation'

interface Props {
  confirmDates(dates: number[], morning: boolean): Promise<void>
  filterEmailDates: number[]
  disabled?: boolean
  defaultDayTime?: boolean
  id?: string // top level id for dom
}

const timeBtnStyle =
  'border rounded-2xl flex place-content-center place-items-center gap-x-2 text-sm flex-1 md:flex-none min-w-[9.5rem] hover:border-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400'

const activeStyles =
  'border-blue-800 text-blue-800 dark:text-blue-500 dark:border-blue-500'

const activeHoverWeekCss =
  'hover:border-blue-600 hover:bg-blue-700 hover:text-white dark:hover:text-blue-600 dark:hover:border-blue-600'

type WeekItemProps = {
  day: string
  selectDates(day: number): Promise<void>
  disabled?: boolean
  selected: number[]
  disabledStyles?: string
  index: number
}

const WeekItem = ({
  day,
  selectDates,
  disabled,
  selected,
  disabledStyles,
  index,
}: WeekItemProps) => {
  const onSelectDate = async () => await selectDates(index)

  return (
    <li>
      <Button
        onClick={onSelectDate}
        className={classNames(
          `text-sm border rounded-3xl flex-1 px-3 md:px-6 py-1 min-w-[4rem]`,
          !disabled
            ? selected.includes(index)
              ? `bg-gray-300 text-gray-600 dark:text-gray-400 dark:bg-gray-700 ${activeHoverWeekCss}`
              : 'hover:text-blue-600 hover:border-blue-600 dark:hover:border-blue-600'
            : '',
          disabledStyles || ''
        )}
        type={'button'}
        disabled={disabled}
      >
        {day}
      </Button>
    </li>
  )
}
const WeekSelectComponent: FC<Props> = ({
  confirmDates,
  filterEmailDates,
  disabled,
  defaultDayTime,
  id,
}) => {
  const { t, lang } = useTranslation('settings')
  const [selected, setSelected] = useState<number[]>(filterEmailDates ?? [])
  const [morning, setMorning] = useState<boolean>(!!defaultDayTime)
  const initialDate = useRef<boolean>(false)
  const initialDayTime = useRef<boolean>(false)

  useEffect(() => {
    if (filterEmailDates && !initialDate.current) {
      setSelected(filterEmailDates)
      initialDate.current = true
    }
    if (defaultDayTime && !initialDayTime.current) {
      setMorning(defaultDayTime)
      initialDayTime.current = true
    }
  }, [filterEmailDates, defaultDayTime, setSelected, initialDate, setMorning])

  const onDateConfirm = useCallback(
    async (period: boolean) => {
      const validItems = selected.filter(
        (item) => Number.isInteger(item) && item >= 0
      )
      await confirmDates(validItems, period)
    },
    [confirmDates, selected]
  )

  const selectDates = useCallback(
    async (day: number) => {
      const items = [...selected]
      const selectedDate = items.indexOf(day)

      if (selectedDate !== -1) {
        items.splice(selectedDate, 1)
      } else {
        items.push(day)
      }

      setSelected(items)

      const validItems = items.filter(
        (item) => Number.isInteger(item) && item >= 0
      )

      // todo - fire on unmount of lose of focus
      await confirmDates(validItems, morning)
    },
    [setSelected, selected, morning, confirmDates]
  )

  const onPeriodToggleMorning = useCallback(async () => {
    setMorning(true)
    await onDateConfirm(true)
  }, [setMorning, onDateConfirm])

  const onPeriodToggleNight = useCallback(async () => {
    setMorning(false)
    await onDateConfirm(false)
  }, [setMorning, onDateConfirm])

  const disabledStyles = !disabled
    ? 'hover:text-blue-600 hover:border-blue-600'
    : 'bg-gray-300 text-gray-600 dark:bg-gray-800 dark:text-gray-600'

  const localMaps = useMemo(() => getDayNames(lang), [lang])

  return (
    <div id={id ?? 'notification-selector'}>
      <ul
        className={'flex gap-x-1 md:gap-x-2 flex-wrap gap-y-2 pb-2.5 list-none'}
      >
        {localMaps.map((day, index) => (
          <WeekItem
            key={day}
            selected={selected}
            selectDates={selectDates}
            disabled={disabled}
            disabledStyles={disabledStyles}
            day={day}
            index={index}
          />
        ))}
      </ul>

      <div className='space-y-3 border-t pt-2'>
        <p className='text-sm text-gray-800 dark:text-gray-200'>
          {t('time-pref')}
        </p>
        <div className='flex space-x-2 '>
          <Button
            onClick={onPeriodToggleMorning}
            className={classNames(
              morning && !disabled
                ? activeStyles
                : 'dark:border-gray-700 dark:text-gray-200',
              timeBtnStyle,
              disabledStyles
            )}
            type='button'
            border={false}
            disabled={disabled}
          >
            <GrSun className={'grIcon'} />
            {t('morning')}
          </Button>
          <Button
            onClick={onPeriodToggleNight}
            className={classNames(
              !morning && !disabled
                ? activeStyles
                : 'dark:border-gray-700 dark:text-gray-200',
              timeBtnStyle,
              disabledStyles
            )}
            border={false}
            type='button'
            disabled={disabled}
          >
            <GrMoon className={'grIcon'} />
            {t('night')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export const WeekSelect = memo(WeekSelectComponent)
